import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { toZonedTime } from 'date-fns-tz'
import { addMinutes, endOfDay, isToday, startOfDay, subMinutes } from 'date-fns'
import moment from 'moment-timezone'

import API from '../../../../api/api'
import {
  fetchInitialData,
  getBuildingOptions,
  getEventTypeOptions,
  getTrainerOptions,
} from '../Steps/data/adminManager.data'
import {
  getCheckboxHandler,
  getFrequencyOptions,
  getIdFromObjectOrValue,
  getOption,
  getTimezone,
  handleTimeChange,
  transformFormStateToRequestData,
} from '../helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import NumericFieldWithIncrement from '../../../FormLegacy/FormFieldsV2/NumericFieldWithIncrement'
import TextAreaField from '../../../FormLegacy/FormFieldsV2/TextAreaField'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import Icon from '../../../Icons'
import Loader from '../../../Loader/Loader'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { useFetchSocialLinks } from '../../hooks'
import BaseEvent from '../BaseEvent/BaseEvent'
import EditEvent from '../EditEvent/EditEvent'
import EditEventsSchedule from '../EditEventsSchedule/EditEventsSchedule'
import ImagesDatabase from '../ImagesDatabase/ImagesDatabase'
import MediaUploadInput from '../MediaUploadInput/MediaUploadInput'
import MediaUploadInputEditMode from '../MediaUploadInputEditMode/MediaUploadInputEditMode'
import MobilePreview from '../../../EventManagement/common/components/mobilePreview/MobilePreview'
import PositiveReviewFollowup from '../PositiveReviewFollowup/PositiveReviewFollowup'
import SetupAutomatedReminders from '../SetupAutomatedReminders/SetupAutomatedReminders'

import { useGetClassFormState } from './hooks'

const ClassEvent = ({
  editMode,
  eventData,
  goBack,
  handleClose,
  repeatable,
  selectedBuildingId,
}) => {
  const { t } = useTranslation(['create-event', 'common'])
  const { displaySnackbar } = useSnackbar()
  const [eventsToUpdate, setEventsToUpdate] = useState('false')
  const [isPopupOpen, togglePopup] = useState(false)
  const [fetchState, setFetchState] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    ;(async () => {
      const data = await fetchInitialData()
      setFetchState((prev) => ({
        ...prev,
        ...data,
      }))
    })()
  }, [])

  const trainerOptions = fetchState.trainers ? getTrainerOptions(fetchState.trainers) : []
  const eventTypeOptions = fetchState.trainingTypes
    ? getEventTypeOptions(fetchState.trainingTypes).filter(
        (opt) => !['Building Event', 'Reminder'].includes(opt.text),
      )
    : []

  const buildingOptions = fetchState.buildings ? getBuildingOptions(fetchState.buildings) : []

  const [formState, setFormState] = useGetClassFormState(
    eventData,
    editMode,
    selectedBuildingId,
    buildingOptions,
    t,
  )
  const socialLinks = useFetchSocialLinks(formState.building)

  const handleFieldChange = ({ target: { name, value } }) => {
    if (name === 'date') {
      const newFrequencyOptions = getFrequencyOptions(value, t)

      setFormState((prev) => {
        if (prev.schedulerType && prev.schedulerType.value !== 'none') {
          const currentType = prev.schedulerType.value
          const updatedOption = newFrequencyOptions.find((opt) => opt.value === currentType)

          return {
            ...prev,
            [name]: value,
            schedulerType: updatedOption || prev.schedulerType,
          }
        }

        // Otherwise just update the date
        return { ...prev, [name]: value }
      })
    } else {
      setFormState((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleCheckboxChange = getCheckboxHandler(handleFieldChange)

  const detailsButtonEnabled =
    !!formState.building &&
    !!formState.eventType &&
    !!formState.trainer &&
    !!formState.eventName &&
    !!formState.description

  const timezone = useMemo(
    () => getTimezone(formState, buildingOptions),
    [buildingOptions, formState.building],
  )

  const { minStartTime, maxStartTime, minEndTime, maxEndTime } = useMemo(() => {
    const selectedDate = new Date(formState.date)

    const minTime = isToday(selectedDate)
      ? toZonedTime(new Date(), timezone)
      : startOfDay(toZonedTime(selectedDate, timezone))
    const maxTime = endOfDay(minTime)

    return {
      minStartTime: minTime,
      maxStartTime: subMinutes(maxTime, 15),
      minEndTime: addMinutes(minTime, 15),
      maxEndTime: maxTime,
    }
  }, [formState.date, timezone])

  useEffect(() => {
    if (editMode && eventData && timezone) {
      setFormState((prev) => ({
        ...prev,
        date: moment(eventData.date).tz(timezone).toDate(),
      }))
    }
  }, [editMode, timezone, eventData])

  const parametersButtonEnabled =
    !!formState.location &&
    !!formState.date &&
    !!formState.timeStart &&
    !!formState.timeEnd &&
    !!formState.eventCapacity > 0

  const onSubmit = async () => {
    setIsSubmitting(true)
    const formData = await transformFormStateToRequestData(
      {
        ...formState,
        buildingData: buildingOptions.find(
          (b) => b.id === getIdFromObjectOrValue(formState.building),
        ),
      },
      'Class',
      eventTypeOptions,
      false,
      socialLinks,
    )
    API.createNewEvent(formData)
      .then(() =>
        displaySnackbar(
          t('create-event:classEvent.createdSuccessfully', { eventName: formState.eventName }),
        ),
      )
      .catch(() => {
        displaySnackbar(
          t('create-event:classEvent.errorCreating', { eventName: formState.eventName }),
          true,
        )
      })
      .finally(() => {
        setIsSubmitting(false)
        handleClose(true)
      })
  }

  const onSubmitEdit = () => {
    if (repeatable) {
      togglePopup(true)
    } else {
      setEventsToUpdate(false)
      editModeConfirmCallback()
    }
  }

  const editModeConfirmCallback = async () => {
    setIsSubmitting(false)
    const newData = await transformFormStateToRequestData(
      {
        ...formState,
        buildingData: buildingOptions.find(
          (b) => b.id === getIdFromObjectOrValue(formState.building),
        ),
      },
      'Class',
      eventTypeOptions,
      false,
      socialLinks,
    )
    togglePopup(false)
    API.updateEvent(eventData.id, newData, eventsToUpdate)
      .then(() => displaySnackbar(t('create-event:shared.changesSavedSuccessfully')))
      .catch(() => displaySnackbar(t('create-event:shared.errorSavingChanges'), true))
      .finally(() => {
        setIsSubmitting(false)
        handleClose(true)
      })
  }

  const editModeCancelCallback = () => {
    togglePopup(false)
  }

  const isLoading = !buildingOptions.length && !eventTypeOptions.length && !trainerOptions.length

  const config = {
    details: {
      header: t('create-event:baseEvent.steps.eventDetails'),
      canGoNext: detailsButtonEnabled,
      inputs: {
        first: {
          render: () => (
            <Select
              className="select-container"
              classNamePrefix="select-primary"
              options={buildingOptions}
              name="building"
              value={getOption(formState.building, buildingOptions)}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  building: e,
                }))
              }
              placeholder={t('create-event:shared.selectBuildingPlaceholder')}
            />
          ),
          label: t('create-event:shared.selectBuildingLabel'),
        },
        second: {
          render: () => (
            <Select
              className="select-container"
              classNamePrefix="select-primary"
              name="eventType"
              options={eventTypeOptions}
              placeholder={t('create-event:classEvent.selectClassType')}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  eventType: e,
                }))
              }
              value={getOption(formState.eventType, eventTypeOptions)}
            />
          ),
          label: t('create-event:classEvent.selectClassType'),
        },
        third: {
          render: () => (
            <Select
              className="select-container"
              classNamePrefix="select-primary"
              name="trainer"
              icon="person"
              options={trainerOptions}
              placeholder={t('create-event:shared.selectTrainerLabel')}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  trainer: e,
                }))
              }
              value={getOption(formState.trainer, trainerOptions)}
            />
          ),
          label: t('create-event:shared.selectTrainerLabel'),
        },
        fourth: {
          render: () => (
            <TextField
              name="eventName"
              onChange={handleFieldChange}
              value={formState.eventName}
              placeholder={t('create-event:classEvent.enterClassNamePlaceholder')}
              maxLength={100}
            />
          ),
          label: t('create-event:classEvent.className'),
        },
        fifth: {
          render: () => (
            <TextAreaField
              name="description"
              onChange={handleFieldChange}
              value={formState.description}
              placeholder={t('create-event:classEvent.classDescriptionPlaceholder')}
              maxLength={1000}
            />
          ),
          label: t('create-event:classEvent.classDescription'),
        },
      },
    },
    mediaUpload: {
      header: t('create-event:progressTimeline.mediaUpload'),
      canGoNext: !!formState.image,
      editMode: editMode,
      inputs: {
        first: {
          label: t('create-event:shared.uploadEventPhotoLabel'),
          render: () =>
            editMode ? (
              <MediaUploadInputEditMode
                file={formState.image}
                handleFieldChange={handleFieldChange}
                buttonLabel={t('create-event:shared.replacePhotoButtonLabel')}
                name="image"
                accept="image/*"
                maxFileNameWidth="150px"
              />
            ) : formState.imagesDatabaseOpen ? (
              <ImagesDatabase formState={formState} handleFieldChange={handleFieldChange} />
            ) : (
              <MediaUploadInput
                eventName={formState.eventName}
                formState={formState}
                handleFieldChange={handleFieldChange}
              />
            ),
        },
        second: null,
        image:
          editMode && formState.image
            ? {
                image:
                  typeof formState.image === 'string'
                    ? getAssetLink({
                        path: 'chitown-images/training',
                        file: formState.image,
                        fallback: { file: 'training_image.png' },
                      })
                    : URL.createObjectURL(formState.image),
              }
            : null,
      },
    },
    dateAndTime: {
      header: t('create-event:progressTimeline.parameters'),
      canGoNext: parametersButtonEnabled,
      inputs: {
        first: {
          render: () => (
            <TextField
              leftIcon={<Icon icon="location2" />}
              name="location"
              placeholder={t('create-event:classEvent.enterClassLocationPlaceholder')}
              onChange={handleFieldChange}
              value={formState.location}
            />
          ),
          label: t('create-event:shared.locationRequiredLabel'),
        },
        second: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              name="date"
              minDate={moment().toDate()}
              value={formState.date}
              onChange={handleFieldChange}
            />
          ),
          label: t('create-event:classEvent.classDateAndTime'),
        },
        third: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minStartTime}
              maxTime={maxStartTime}
              name="timeStart"
              value={formState.timeStart}
              onChange={(e) =>
                handleTimeChange(e, handleFieldChange, formState.timeStart, formState.timeEnd)
              }
            />
          ),
          label: t('create-event:shared.startsLabel'),
        },
        fourth: {
          render: () => (
            <DateField
              popperPlacement="bottom-start"
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="h:mm aa"
              minTime={minEndTime}
              maxTime={maxEndTime}
              name="timeEnd"
              value={formState.timeEnd}
              onChange={(e) =>
                handleTimeChange(e, handleFieldChange, formState.timeStart, formState.timeEnd)
              }
            />
          ),
          label: t('create-event:shared.endsLabel'),
        },
        fifth: editMode
          ? null
          : {
              render: () => (
                <Select
                  className="select-container"
                  classNamePrefix="select-primary"
                  options={getFrequencyOptions(formState.date, t)}
                  name="schedulerType"
                  placeholder={t('create-event:shared.selectFrequencyPlaceholder')}
                  value={formState.schedulerType}
                  onChange={(e) => setFormState((prev) => ({ ...prev, schedulerType: e }))}
                  selectStyle={{ width: '100%' }}
                />
              ),
              label: t('create-event:shared.frequencyLabel'),
            },
        sixth: {
          render: () => (
            <NumericFieldWithIncrement
              onlyPositive
              leftIcon={<Icon icon="twoPeople" />}
              isError={formState.eventCapacity < 1}
              onIncrement={handleFieldChange}
              onDecrement={handleFieldChange}
              name="eventCapacity"
              onChange={handleFieldChange}
              value={formState.eventCapacity}
              containerStyle={{
                width: '30%',
              }}
            />
          ),
          label: t('create-event:classEvent.enterClassCapacity'),
        },
        seventh: null,
      },
    },
    summary: {
      header: t('create-event:baseEvent.steps.attendees'),
      canGoNext: !isSubmitting,
      onSubmit: editMode ? onSubmitEdit : onSubmit,
      inputs: {
        first: null,
        second: {
          render: () => (
            <CheckboxField
              name="askForPostEventReview"
              onChange={handleCheckboxChange}
              value={formState.askForPostEventReview}
            />
          ),
          renderExtra: () =>
            formState.askForPostEventReview && (
              <PositiveReviewFollowup
                options={socialLinks || []}
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
                formState={formState}
                tooltips={t('create-event:shared.positiveReviewFollowupTooltip', {
                  returnObjects: true,
                })}
              />
            ),
          label: t('create-event:shared.askForPostEventReviewLabel'),
          tooltips: t('create-event:shared.askForPostEventReviewTooltip', { returnObjects: true }),
        },
        third: {
          render: () => (
            <CheckboxField
              name="setupAutomatedReminders"
              onChange={handleCheckboxChange}
              value={formState.setupAutomatedReminders}
            />
          ),
          renderExtra: () =>
            formState.setupAutomatedReminders && (
              <SetupAutomatedReminders
                formState={formState}
                handleCheckboxChange={handleCheckboxChange}
                handleFieldChange={handleFieldChange}
              />
            ),
          label: t('create-event:shared.setupAutomatedRemindersLabel'),
          tooltips: t('create-event:shared.setupAutomatedRemindersTooltip', {
            returnObjects: true,
          }),
        },
        fourth: null,
        fifth: null,
        sixth: null,
      },
    },
    footer: {
      cta: editMode
        ? t('create-event:shared.saveChangesCta')
        : t('create-event:classEvent.createAClassCta'),
    },
  }
  return (
    <div className="events-modal">
      <div className="events-modal-card">
        {isLoading ? (
          <Loader
            style={{
              width: '100px',
              height: '100px',
              left: '44%',
              top: '40%',
            }}
          />
        ) : editMode ? (
          <EditEvent config={config} eventType="class" handleClose={handleClose} />
        ) : (
          <BaseEvent config={config} goBack={goBack} eventType="class" handleClose={handleClose} />
        )}
        <ConfirmPopup
          isOpen={isPopupOpen}
          cancelCallback={editModeCancelCallback}
          confirmCallback={editModeConfirmCallback}
          cancelText={t('common:actions.cancel')}
          confirmText={t('common:actions.confirm')}
          title={t('create-event:shared.cautionTitle')}
          body={
            <EditEventsSchedule
              onChange={(e) => setEventsToUpdate(e.target.value)}
              value={eventsToUpdate}
              formName="classEvent"
            />
          }
        />
      </div>
      <MobilePreview formState={formState} eventType="Class" trainerOptions={trainerOptions} />
    </div>
  )
}

ClassEvent.propTypes = {
  editMode: PropTypes.bool,
  eventData: PropTypes.object,
  goBack: PropTypes.func,
  handleClose: PropTypes.func,
  repeatable: PropTypes.bool,
  selectedBuildingId: PropTypes.number,
}

export default connect((state) => {
  const selectedBuilding = selectActiveBuilding(state)
  return {
    selectedBuildingId: selectedBuilding ? selectedBuilding.id : null,
  }
})(ClassEvent)
